import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { ReportsService } from '../../../services/reports.service';
import { CrmTarget } from '../crm-notes-panel/crm-notes-panel.component';

interface RunnerStop {
	stop_id: number;
	day_of_week: number;
	position: number;
	priority: number;
	stop_type: 'customer' | 'prospect';
	prospect_key: string;
	stop_status: 'pending' | 'done' | 'skipped';
	status_at: string | null;
	debtorno: string;
	branchcode: string;
	name: string;
	brname: string;
	address: string;
	city: string;
	state: string;
	zip: string;
	lat: number | null;
	lng: number | null;
	phoneno: string | null;
	last_visit_date: string | null;
	last_order_date: string | null;
}

interface NearbyRow {
	kind: 'customer' | 'prospect';
	key: string;
	name: string;
	sub: string;
	address: string;
	city: string;
	distance_miles: number;
	lat: number;
	lng: number;
	phoneno?: string;
	debtorno?: string;
	branchcode?: string;
	prospect_key?: string;
	est_potential?: number;
	est_twin?: string;
	license_type?: string;
	last_visit_date?: string | null;
	last_order_date?: string | null;
	is_mine?: boolean;
	assigned?: boolean;
}

@Component({
	selector: 'app-sales-route-runner',
	templateUrl: './sales-route-runner.component.html',
	styleUrls: ['./sales-route-runner.component.scss']
})
export class SalesRouteRunnerComponent implements OnInit, OnDestroy {
	config: any;
	loading = false;
	status: string | null = null;

	salesman = '';
	salesmanname = '';
	weekStart = '';
	activeDow = 0;
	readonly days = [
		{ dow: 0, label: 'Mon' }, { dow: 1, label: 'Tue' }, { dow: 2, label: 'Wed' },
		{ dow: 3, label: 'Thu' }, { dow: 4, label: 'Fri' },
	];

	allStops: RunnerStop[] = [];

	// Check-in modal
	checkinStop: RunnerStop | null = null;
	checkinOutcome = 'met';
	checkinNotes = '';
	checkingIn = false;
	checkinGeo: { lat: number; lng: number } | null = null;
	readonly checkinOutcomes = [
		{ v: 'met', l: 'Met with customer' },
		{ v: 'no_contact', l: 'No contact' },
		{ v: 'left_sample', l: 'Left sample' },
		{ v: 'called', l: 'Called' },
		{ v: 'other', l: 'Other' },
	];

	// CRM notes sheet
	crmTarget: CrmTarget | null = null;

	// Nearby
	showNearby = false;
	nearbyLoading = false;
	nearbyTab: 'customers' | 'prospects' = 'customers';
	nearbyCustomers: NearbyRow[] = [];
	nearbyProspects: NearbyRow[] = [];
	nearbyRadius = 10;
	nearbyOrigin: { lat: number; lng: number; label: string } | null = null;

	showDone = false;
	locating = false;

	private userSub?: Subscription;

	constructor(
		public globalSearchService: GlobalSearchService,
		private reportsService: ReportsService,
		private route: ActivatedRoute,
		private router: Router,
		private locationSvc: Location,
		private cdr: ChangeDetectorRef
	) {
		this.globalSearchService.mesagesallowed.next(false);
		this.globalSearchService.configsubscription.subscribe((r) => {
			this.config = r;
			this.globalSearchService.getColor();
		});
	}

	ngOnInit(): void {
		this.globalSearchService.getColor();
		const qp = this.route.snapshot.queryParamMap;
		this.salesman = (qp.get('salesman') || '').trim();
		this.weekStart = (qp.get('week') || '').trim() || this.defaultWeekStart();
		const dowParam = qp.get('dow');
		this.activeDow = dowParam != null ? this.clampDow(parseInt(dowParam, 10)) : this.todayDow();

		if (!this.salesman) {
			this.userSub = this.globalSearchService.user.subscribe((u: any) => {
				const code = String(u?.user?.salesman ?? '').trim();
				if (code && !this.salesman) {
					this.salesman = code;
					this.loadWeek();
				}
			});
		} else {
			this.loadWeek();
		}
	}

	ngOnDestroy(): void { this.userSub?.unsubscribe(); }

	back(): void { this.locationSvc.back(); }

	openPlanner(): void {
		this.router.navigate(['/customers/sales-route-builder']);
	}

	/** On a weekend, "my route" means next week's Monday — not last Friday. */
	private defaultWeekStart(): string {
		const now = new Date();
		const dow = now.getDay(); // 0=Sun … 6=Sat
		if (dow === 0 || dow === 6) {
			const next = new Date(now);
			next.setDate(next.getDate() + (dow === 6 ? 2 : 1));
			return this.mondayOf(next);
		}
		return this.mondayOf(now);
	}
	private todayDow(): number {
		const d = new Date().getDay(); // 0=Sun
		if (d === 0 || d === 6) { return 0; } // weekend → Monday of the rolled-forward week
		return this.clampDow(d - 1);
	}
	private clampDow(d: number): number { return Number.isFinite(d) ? Math.min(4, Math.max(0, d)) : 0; }
	/** Is this weekday chip already behind us? */
	isPastDay(dow: number): boolean {
		if (!this.weekStart) { return false; }
		const [y, m, d] = this.weekStart.split('-').map((n) => parseInt(n, 10));
		const day = new Date(y, m - 1, d + dow);
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		return day.getTime() < today.getTime();
	}
	/** Days worth showing on the road: today forward (past days aren't actionable). */
	get visibleDays() {
		const upcoming = this.days.filter((d) => !this.isPastDay(d.dow));
		return upcoming.length ? upcoming : this.days;
	}
	dayDate(dow: number): string {
		if (!this.weekStart) { return ''; }
		const [y, m, d] = this.weekStart.split('-').map((n) => parseInt(n, 10));
		const dt = new Date(y, m - 1, d + dow);
		return `${dt.getMonth() + 1}/${dt.getDate()}`;
	}
	private mondayOf(d: Date): string {
		const cp = new Date(d);
		const dow = cp.getDay();
		const back = dow === 0 ? 6 : dow - 1;
		cp.setHours(0, 0, 0, 0);
		cp.setDate(cp.getDate() - back);
		return `${cp.getFullYear()}-${String(cp.getMonth() + 1).padStart(2, '0')}-${String(cp.getDate()).padStart(2, '0')}`;
	}

	loadWeek(): void {
		if (!this.salesman || !this.weekStart) { return; }
		this.loading = true;
		this.reportsService.getSalesRouteWeek(this.salesman, this.weekStart).subscribe({
			next: (res: any) => {
				this.loading = false;
				this.allStops = (res?.success && Array.isArray(res.stops)) ? res.stops.map((o: any) => this.normStop(o)) : [];
				this.salesmanname = res?.route?.salesperson || this.salesmanname;
				this.cdr.markForCheck();
			},
			error: () => { this.loading = false; this.status = 'Could not load the route.'; this.cdr.markForCheck(); }
		});
	}

	private normStop(o: any): RunnerStop {
		const lat = parseFloat(String(o.lat ?? ''));
		const lng = parseFloat(String(o.lng ?? ''));
		return {
			stop_id: Number(o.stop_id ?? 0),
			day_of_week: Number(o.day_of_week ?? 0),
			position: Number(o.position ?? 0),
			priority: Number(o.priority ?? 2),
			stop_type: o.stop_type === 'prospect' ? 'prospect' : 'customer',
			prospect_key: String(o.prospect_key ?? ''),
			stop_status: (o.stop_status === 'done' || o.stop_status === 'skipped') ? o.stop_status : 'pending',
			status_at: o.status_at ?? null,
			debtorno: String(o.debtorno ?? ''),
			branchcode: String(o.branchcode ?? ''),
			name: String(o.name ?? ''),
			brname: String(o.brname ?? ''),
			address: String(o.address ?? ''),
			city: String(o.city ?? ''),
			state: String(o.state ?? ''),
			zip: String(o.zip ?? ''),
			lat: Number.isFinite(lat) && lat !== 0 ? lat : null,
			lng: Number.isFinite(lng) && lng !== 0 ? lng : null,
			phoneno: o.phoneno ?? null,
			last_visit_date: o.last_visit_date ?? null,
			last_order_date: o.last_order_date ?? null,
		};
	}

	// ── day views ───────────────────────────────────────────────────
	selectDay(dow: number): void { this.activeDow = dow; this.showDone = false; }
	get dayStops(): RunnerStop[] {
		return this.allStops.filter((s) => s.day_of_week === this.activeDow)
			.sort((a, b) => a.position - b.position);
	}
	dayCount(dow: number): number { return this.allStops.filter((s) => s.day_of_week === dow).length; }
	get pendingStops(): RunnerStop[] { return this.dayStops.filter((s) => s.stop_status === 'pending'); }
	get finishedStops(): RunnerStop[] { return this.dayStops.filter((s) => s.stop_status !== 'pending'); }
	get currentStop(): RunnerStop | null { return this.pendingStops[0] || null; }
	get upNextStops(): RunnerStop[] { return this.pendingStops.slice(1); }
	get doneCount(): number { return this.dayStops.filter((s) => s.stop_status === 'done').length; }
	get progressPct(): number {
		const total = this.dayStops.length;
		return total ? Math.round((this.finishedStops.length / total) * 100) : 0;
	}
	get dayComplete(): boolean { return this.dayStops.length > 0 && !this.pendingStops.length; }

	/** Rough drive miles for the day's ordered stops (stop-to-stop chain). */
	get dayMiles(): number {
		const pts = this.dayStops.filter((s) => s.lat != null && s.lng != null);
		if (pts.length < 2) { return 0; }
		const R = 3958.8, toRad = (d: number) => (d * Math.PI) / 180;
		let total = 0;
		for (let i = 1; i < pts.length; i++) {
			const dLat = toRad(pts[i].lat! - pts[i - 1].lat!), dLng = toRad(pts[i].lng! - pts[i - 1].lng!);
			const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(pts[i - 1].lat!)) * Math.cos(toRad(pts[i].lat!)) * Math.sin(dLng / 2) ** 2;
			total += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		}
		return total;
	}

	stopLabel(s: RunnerStop): string {
		const parts = [s.name];
		if (s.brname && s.brname.trim() && s.brname !== s.name) { parts.push(s.brname); }
		return parts.filter(Boolean).join(' — ');
	}
	stopAddress(s: RunnerStop): string {
		return [s.address, s.city].filter((x) => String(x || '').trim()).join(', ');
	}
	priorityLabel(p: number): string { return p === 1 ? 'High' : p === 3 ? 'Low' : ''; }

	// ── actions ─────────────────────────────────────────────────────
	navigateTo(s: RunnerStop | NearbyRow): void {
		let dest = '';
		const lat = (s as any).lat, lng = (s as any).lng;
		if (lat != null && lng != null && Number.isFinite(Number(lat)) && Number(lat) !== 0) {
			dest = `${lat},${lng}`;
		} else {
			dest = [(s as any).address, (s as any).city, (s as any).state, (s as any).zip]
				.filter((x) => String(x || '').trim()).join(', ');
		}
		if (!dest) { this.status = 'No address or GPS on this stop.'; return; }
		window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`, '_blank');
	}

	telHref(phone: string | null | undefined): string {
		return 'tel:' + String(phone || '').replace(/[^0-9+]/g, '');
	}

	openCheckin(s: RunnerStop): void {
		this.checkinStop = s;
		this.checkinOutcome = 'met';
		this.checkinNotes = '';
		this.checkinGeo = null;
		// Grab the rep's location in the background; check-in works without it.
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(pos) => { this.checkinGeo = { lat: pos.coords.latitude, lng: pos.coords.longitude }; this.cdr.markForCheck(); },
				() => { /* fine without geo */ },
				{ enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
			);
		}
	}
	cancelCheckin(): void { this.checkinStop = null; }

	doCheckin(): void {
		const s = this.checkinStop;
		if (!s) { return; }
		this.checkingIn = true;
		this.reportsService.postSalesRouteCheckin({
			debtorno: s.stop_type === 'customer' ? s.debtorno : '',
			branchcode: s.stop_type === 'customer' ? s.branchcode : '',
			prospect_key: s.stop_type === 'prospect' ? s.prospect_key : '',
			salesperson: this.salesman,
			outcome: this.checkinOutcome,
			notes: this.checkinNotes,
			visit_lat: this.checkinGeo?.lat,
			visit_lng: this.checkinGeo?.lng,
			stop_id: s.stop_id,
		}).subscribe({
			next: (res: any) => {
				this.checkingIn = false;
				if (res?.success) {
					s.stop_status = 'done';
					s.last_visit_date = res.visit_date || s.last_visit_date;
					this.status = null;
				} else {
					this.status = res?.message || 'Check-in failed.';
				}
				this.checkinStop = null;
				this.cdr.markForCheck();
			},
			error: () => { this.checkingIn = false; this.status = 'Check-in failed.'; this.checkinStop = null; this.cdr.markForCheck(); }
		});
	}

	setStatus(s: RunnerStop, status: 'pending' | 'done' | 'skipped'): void {
		const prev = s.stop_status;
		s.stop_status = status;
		this.reportsService.postSalesRouteStopStatus({ stop_id: s.stop_id, status }).subscribe({
			next: (res: any) => { if (!res?.success) { s.stop_status = prev; this.status = 'Could not update the stop.'; } this.cdr.markForCheck(); },
			error: () => { s.stop_status = prev; this.status = 'Could not update the stop.'; this.cdr.markForCheck(); }
		});
	}

	openNotes(s: RunnerStop): void {
		this.crmTarget = s.stop_type === 'prospect'
			? { prospect_key: s.prospect_key, name: s.name }
			: { debtorno: s.debtorno, branchcode: s.branchcode, name: s.name };
	}
	openNotesNearby(r: NearbyRow): void {
		this.crmTarget = r.kind === 'prospect'
			? { prospect_key: r.prospect_key, name: r.name }
			: { debtorno: r.debtorno, branchcode: r.branchcode, name: r.name };
	}
	closeNotes(): void { this.crmTarget = null; }

	/** Multi-waypoint Google Maps link for the day's remaining stops (cap 10). */
	openDayInMaps(): void {
		const pts = this.pendingStops.filter((s) => s.lat != null && s.lng != null).slice(0, 10);
		if (!pts.length) { return; }
		const dest = `${pts[pts.length - 1].lat},${pts[pts.length - 1].lng}`;
		const way = pts.slice(0, -1).map((p) => `${p.lat},${p.lng}`).join('|');
		const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}` + (way ? `&waypoints=${encodeURIComponent(way)}` : '');
		window.open(url, '_blank');
	}

	// ── create straight from the road ───────────────────────────────
	/** Open Order Entry for a customer stop / nearby customer. */
	createOrder(s: RunnerStop | NearbyRow): void {
		const dn = (s as any).debtorno, bc = (s as any).branchcode || '';
		if (!dn) { this.status = 'No customer account on this stop.'; return; }
		this.router.navigate(['/orders/entry/' + dn + '/' + bc]);
	}
	/** Convert a lead into a customer (reuses the Salon Prospect → Convert prefill). */
	createCustomer(s: { name: string; address: string; city: string; state?: string; zip?: string }): void {
		this.router.navigate(['/customers/create'], {
			state: { salonProspect: { name: s.name, address1: s.address, city: s.city, state: s.state || 'NY', zip: s.zip || '' } }
		});
	}

	// ── nearby ──────────────────────────────────────────────────────
	toggleNearby(): void {
		this.showNearby = !this.showNearby;
		if (this.showNearby) {
			if (!this.nearbyCustomers.length && !this.nearbyProspects.length) { this.refreshNearby(); }
			// The section renders below the stop lists — bring it into view.
			setTimeout(() => document.getElementById('srrNearby')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
		}
	}

	refreshNearby(): void {
		this.locating = true;
		this.status = null;
		const fallback = () => {
			const s = this.currentStop || this.dayStops.find((x) => x.lat != null);
			if (s && s.lat != null && s.lng != null) {
				this.runNearby(s.lat, s.lng, s.name);
			} else {
				this.locating = false;
				this.status = 'No location available — add a stop with GPS or allow location access.';
				this.cdr.markForCheck();
			}
		};
		if (!navigator.geolocation) { fallback(); return; }
		navigator.geolocation.getCurrentPosition(
			(pos) => this.runNearby(pos.coords.latitude, pos.coords.longitude, 'your location'),
			() => fallback(),
			{ enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
		);
	}

	private runNearby(lat: number, lng: number, label: string): void {
		this.locating = false;
		this.nearbyLoading = true;
		this.nearbyOrigin = { lat, lng, label };
		this.reportsService.postSalesRouteNearby({
			lat, lng, radius_miles: this.nearbyRadius, limit: 25, salesman: this.salesman,
		}).subscribe({
			next: (res: any) => {
				this.nearbyLoading = false;
				const onRoute = new Set(this.allStops.map((s) => s.stop_type === 'prospect' ? 'p|' + s.prospect_key : 'c|' + s.debtorno + '|' + s.branchcode));
				this.nearbyCustomers = (res?.customers || [])
					.filter((c: any) => !onRoute.has('c|' + c.debtorno + '|' + c.branchcode))
					.map((c: any): NearbyRow => ({
						kind: 'customer', key: 'c|' + c.debtorno + '|' + c.branchcode,
						name: String(c.name || ''), sub: c.brname && c.brname !== c.name ? String(c.brname) : '',
						address: String(c.address || ''), city: String(c.city || ''),
						distance_miles: Number(c.distance_miles || 0),
						lat: Number(c.lat), lng: Number(c.lng),
						phoneno: c.phoneno, debtorno: String(c.debtorno), branchcode: String(c.branchcode),
						last_visit_date: c.last_visit_date ?? null,
						last_order_date: c.last_order_date ?? null,
						is_mine: Number(c.is_mine || 0) === 1,
					}));
				this.nearbyProspects = (res?.prospects || [])
					.filter((p: any) => !onRoute.has('p|' + p.prospect_key))
					.map((p: any): NearbyRow => ({
						kind: 'prospect', key: 'p|' + p.prospect_key,
						name: String(p.business_name || p.owner || ''), sub: String(p.owner || ''),
						address: String(p.address || ''), city: String(p.city || ''),
						distance_miles: Number(p.distance_miles || 0),
						lat: Number(p.lat), lng: Number(p.lng),
						prospect_key: String(p.prospect_key),
						est_potential: p.est_potential != null ? Number(p.est_potential) : undefined,
						est_twin: p.est_twin || '',
						license_type: this.licenseLabel(String(p.license_type || '')),
						last_visit_date: p.last_visit_date ?? null,
						assigned: Number(p.assigned ?? 0) === 1,
					}));
				if (!this.nearbyCustomers.length && this.nearbyProspects.length) { this.nearbyTab = 'prospects'; }
				this.cdr.markForCheck();
			},
			error: () => { this.nearbyLoading = false; this.status = 'Could not load nearby accounts.'; this.cdr.markForCheck(); }
		});
	}

	licenseLabel(t: string): string {
		if (t.indexOf('BARB') >= 0) { return 'Barber shop'; }
		if (t.indexOf('RENTER') >= 0) { return 'Booth renter'; }
		if (t.indexOf('BUSINESS') >= 0) { return 'Salon'; }
		return t;
	}

	checkinNearby(r: NearbyRow): void {
		// Reuse the stop check-in modal with a synthetic stop (stop_id 0 = no route mark).
		this.checkinStop = {
			stop_id: 0, day_of_week: this.activeDow, position: 0, priority: 2,
			stop_type: r.kind, prospect_key: r.prospect_key || '',
			stop_status: 'pending', status_at: null,
			debtorno: r.debtorno || '', branchcode: r.branchcode || '',
			name: r.name, brname: r.sub, address: r.address, city: r.city, state: '', zip: '',
			lat: r.lat, lng: r.lng, phoneno: r.phoneno || null,
			last_visit_date: r.last_visit_date || null, last_order_date: r.last_order_date || null,
		};
		this.checkinOutcome = 'met';
		this.checkinNotes = '';
		this.checkinGeo = null;
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(pos) => { this.checkinGeo = { lat: pos.coords.latitude, lng: pos.coords.longitude }; this.cdr.markForCheck(); },
				() => { /* fine */ },
				{ enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
			);
		}
	}

	nearbyAddress(r: NearbyRow): string {
		return [r.address, r.city].filter((x) => String(x || '').trim()).join(', ');
	}

	private daysSince(dateStr: string | null | undefined): number | null {
		if (!dateStr) { return null; }
		const d = new Date(String(dateStr).slice(0, 10) + 'T00:00:00');
		if (isNaN(d.getTime())) { return null; }
		return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
	}
	/** "Visited 3d ago" / "Ordered 12d ago" / "Never" from last visit OR order. */
	contactBadge(visit: string | null | undefined, order: string | null | undefined): string {
		const v = this.daysSince(visit), o = this.daysSince(order);
		if (v == null && o == null) { return 'Never'; }
		const useVisit = v != null && (o == null || v <= o);
		const days = useVisit ? v! : o!;
		const kind = useVisit ? 'Visited' : 'Ordered';
		return days === 0 ? `${kind} today` : `${kind} ${days}d ago`;
	}
	neverContacted(s: { last_visit_date?: string | null; last_order_date?: string | null }): boolean {
		return !s.last_visit_date && !s.last_order_date;
	}
}
